import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import AnalyticsPanel from "@/components/AnalyticsPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Trash2, Plus, LogOut, Edit3, Lock, Mail, Check, AlertTriangle, RefreshCw, Upload, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";

const EMPTY = {
  event: { type: "match", title: "", date: "", time: "", location: "", opponent: "", category: "U15", notes: "" },
  news: { title: "", excerpt: "", content: "", image_url: "", author: "Le Staff" },
  faq: { question: "", answer: "", category: "Général", order: 0 },
  document: { title: "", description: "", url: "", category: "Général" },
  contact: { name: "", role: "", phone: "", email: "", photo_url: "", order: 0 },
  detection: { date: "", start_time: "", end_time: "", categories: "", location: "Stade de la Viabert", notes: "" },
  social: { platform: "tiktok", handle: "", url: "", order: 0 },
  liveMatch: { date: "", kickoff_time: "", category: "", opponent: "", is_home: true, location: "Stade de la Viabert", competition: "Championnat", score_us: null, score_them: null, status: "scheduled", summary: "", pinned: false },
};

const CATEGORIES = ["U7", "U9", "U11", "U13", "U15", "U17", "Seniors", "Équipe première"];

function Section({ title, children, action }) {
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-lg p-5 lg:p-6">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-bold text-[#0F172A]" style={{ fontFamily: "Outfit" }}>{title}</h2>
        {action}
      </div>
      {children}
    </div>
  );
}

function LiveMatchCard({ match, onRefresh }) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState({
    score_us: match.score_us ?? "",
    score_them: match.score_them ?? "",
    status: match.status,
    pinned: match.pinned,
    summary: match.summary || "",
  });
  const [mediaUrl, setMediaUrl] = useState("");
  const [mediaType, setMediaType] = useState("youtube");
  const [mediaCaption, setMediaCaption] = useState("");
  const [mediaMinute, setMediaMinute] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const home = match.is_home ? "VUFC" : match.opponent;
  const away = match.is_home ? match.opponent : "VUFC";
  const statusLabel = { scheduled: "À venir", live: "🔴 En direct", finished: "Terminé" }[match.status];
  const statusColor = {
    scheduled: "bg-slate-200 text-slate-700",
    live: "bg-[#DC2626] text-white animate-pulse",
    finished: "bg-slate-700 text-white",
  }[match.status];

  const saveMeta = async () => {
    try {
      await api.put(`/live-matches/${match.id}`, {
        date: match.date,
        kickoff_time: match.kickoff_time,
        category: match.category,
        opponent: match.opponent,
        is_home: match.is_home,
        location: match.location,
        competition: match.competition,
        score_us: editing.score_us === "" ? null : parseInt(editing.score_us, 10),
        score_them: editing.score_them === "" ? null : parseInt(editing.score_them, 10),
        status: editing.status,
        summary: editing.summary,
        pinned: editing.pinned,
        media: match.media || [],
      });
      toast.success("Match mis à jour");
      onRefresh();
    } catch { toast.error("Erreur mise à jour"); }
  };

  const addUrl = async () => {
    if (!mediaUrl.trim()) return toast.error("URL manquante");
    try {
      await api.post(`/live-matches/${match.id}/media/url`, {
        url: mediaUrl, type: mediaType, caption: mediaCaption,
        minute: mediaMinute ? parseInt(mediaMinute, 10) : null,
      });
      setMediaUrl(""); setMediaCaption(""); setMediaMinute("");
      toast.success("Vidéo ajoutée");
      onRefresh();
    } catch { toast.error("Erreur ajout URL"); }
  };

  const uploadFile = async (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    // Guard against the 100MB nginx body limit (common preview default)
    const MAX_MB = 95;
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(`Fichier trop lourd (${(file.size / 1024 / 1024).toFixed(1)} Mo). Max : ${MAX_MB} Mo. Compresse ou poste sur YouTube puis colle le lien.`);
      ev.target.value = "";
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("caption", mediaCaption);
    if (mediaMinute) fd.append("minute", mediaMinute);
    setUploading(true);
    try {
      // Let the browser set Content-Type with the correct multipart boundary
      // by overriding axios instance default with undefined.
      await api.post(`/live-matches/${match.id}/media`, fd, {
        headers: { "Content-Type": undefined },
        timeout: 180_000, // 3 min for larger videos
      });
      setMediaCaption(""); setMediaMinute("");
      ev.target.value = "";
      toast.success("Fichier uploadé");
      onRefresh();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 413) toast.error("Fichier trop volumineux pour le serveur.");
      else if (detail) toast.error(`Erreur : ${detail}`);
      else toast.error("Erreur upload — réessaie ou utilise un lien YouTube.");
    }
    finally { setUploading(false); }
  };

  const removeMedia = async (idx) => {
    if (!window.confirm("Supprimer ce média ?")) return;
    try {
      await api.delete(`/live-matches/${match.id}/media/${idx}`);
      toast.success("Média supprimé");
      onRefresh();
    } catch { toast.error("Erreur suppression"); }
  };

  const del = async () => {
    if (!window.confirm("Supprimer définitivement ce match et toutes ses vidéos ?")) return;
    try {
      await api.delete(`/live-matches/${match.id}`);
      toast.success("Match supprimé");
      onRefresh();
    } catch { toast.error("Erreur"); }
  };

  const scoreTxt = (match.score_us != null && match.score_them != null) ? `${match.score_us} – ${match.score_them}` : "VS";

  return (
    <div className="border border-[#E2E8F0] rounded-md bg-white overflow-hidden" data-testid={`lm-${match.id}`}>
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full p-4 flex items-center justify-between gap-4 hover:bg-slate-50 transition-colors text-left"
        data-testid={`lm-toggle-${match.id}`}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`px-2 py-0.5 text-[10px] font-bold rounded ${statusColor}`}>{statusLabel}</span>
            <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-slate-200 text-slate-700">{match.category}</span>
            <span className="px-2 py-0.5 text-[10px] font-bold rounded border border-[#CBD5E1] text-[#475569]">{match.competition}</span>
            {match.pinned && <span className="text-[10px] font-bold text-[#D97706]">📌 Épinglé</span>}
          </div>
          <div className="mt-1.5 font-bold text-[#0F172A]" style={{ fontFamily: "Outfit" }}>
            {home} <span className="text-[#D97706] mx-1">{scoreTxt}</span> {away}
          </div>
          <div className="text-xs text-[#475569]">
            {match.date}{match.kickoff_time ? ` · ${match.kickoff_time}` : ""} · {match.location} · {match.media?.length || 0} média(s)
          </div>
        </div>
        <span className="text-xs text-[#1E3A8A] font-semibold shrink-0">{expanded ? "Fermer" : "Éditer"}</span>
      </button>

      {expanded && (
        <div className="p-5 bg-slate-50 border-t border-[#E2E8F0] space-y-6">
          {/* Score & status */}
          <div className="grid sm:grid-cols-5 gap-3 items-end">
            <div>
              <Label className="text-xs">Score VUFC</Label>
              <Input type="number" min="0" value={editing.score_us} onChange={(e) => setEditing((s) => ({ ...s, score_us: e.target.value }))} data-testid={`lm-score-us-${match.id}`} />
            </div>
            <div>
              <Label className="text-xs">Score adverse</Label>
              <Input type="number" min="0" value={editing.score_them} onChange={(e) => setEditing((s) => ({ ...s, score_them: e.target.value }))} data-testid={`lm-score-them-${match.id}`} />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs">Statut</Label>
              <Select value={editing.status} onValueChange={(v) => setEditing((s) => ({ ...s, status: v }))}>
                <SelectTrigger data-testid={`lm-status-${match.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="scheduled">À venir</SelectItem>
                  <SelectItem value="live">🔴 En direct</SelectItem>
                  <SelectItem value="finished">Terminé</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 text-xs text-[#475569] cursor-pointer">
              <input type="checkbox" checked={editing.pinned} onChange={(e) => setEditing((s) => ({ ...s, pinned: e.target.checked }))} className="w-4 h-4 accent-[#D97706]" data-testid={`lm-pinned-${match.id}`} />
              Épinglé "À la une"
            </label>
          </div>
          <div>
            <Label className="text-xs">Résumé du match (optionnel)</Label>
            <Textarea rows={2} value={editing.summary} onChange={(e) => setEditing((s) => ({ ...s, summary: e.target.value }))} placeholder="Ex: Beau match remporté sous la pluie, hat-trick de Lucas." data-testid={`lm-summary-${match.id}`} />
          </div>
          <div className="flex gap-2">
            <Button onClick={saveMeta} className="bg-[#1E3A8A] hover:bg-[#1E40AF]" data-testid={`lm-save-${match.id}`}>Enregistrer le score / statut</Button>
            <Button variant="ghost" onClick={del} data-testid={`lm-delete-${match.id}`}><Trash2 size={14} className="text-[#DC2626]" /> Supprimer le match</Button>
          </div>

          {/* Media list */}
          {match.media && match.media.length > 0 && (
            <div>
              <Label className="text-xs mb-2 block">Médias ({match.media.length})</Label>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {match.media.map((m, i) => (
                  <div key={i} className="border border-[#E2E8F0] rounded-md p-2 bg-white flex items-start justify-between gap-2" data-testid={`lm-media-${match.id}-${i}`}>
                    <div className="text-xs flex-1 min-w-0">
                      <div className="font-semibold uppercase text-[#1E3A8A]">{m.type}</div>
                      <div className="text-[#475569] truncate">{m.caption || m.url}</div>
                      {m.minute != null && <div className="text-[#D97706] font-bold">⚽ {m.minute}'</div>}
                    </div>
                    <button onClick={() => removeMedia(i)} className="text-[#DC2626] shrink-0 p-1" data-testid={`lm-remove-media-${match.id}-${i}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add media */}
          <div className="pt-4 border-t border-[#E2E8F0] space-y-3">
            <Label className="text-sm font-bold text-[#0F172A]">Ajouter une vidéo / photo</Label>
            <div className="grid sm:grid-cols-3 gap-2">
              <Input placeholder="Titre (ex: But de Lucas)" value={mediaCaption} onChange={(e) => setMediaCaption(e.target.value)} data-testid={`lm-caption-${match.id}`} />
              <Input type="number" min="0" max="130" placeholder="Minute (23')" value={mediaMinute} onChange={(e) => setMediaMinute(e.target.value)} data-testid={`lm-minute-${match.id}`} />
              <Select value={mediaType} onValueChange={setMediaType}>
                <SelectTrigger data-testid={`lm-mtype-${match.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="youtube">Lien YouTube</SelectItem>
                  <SelectItem value="tiktok">Lien TikTok</SelectItem>
                  <SelectItem value="video">Lien vidéo direct</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input placeholder="Colle ici le lien YouTube / TikTok" value={mediaUrl} onChange={(e) => setMediaUrl(e.target.value)} className="flex-1" data-testid={`lm-url-${match.id}`} />
              <Button onClick={addUrl} className="bg-[#D97706] hover:bg-[#B45309]" data-testid={`lm-add-url-${match.id}`}>Ajouter le lien</Button>
            </div>
            <div className="flex items-center gap-2 pt-2">
              <input ref={fileInputRef} type="file" accept="video/*,image/*" onChange={uploadFile} className="hidden" data-testid={`lm-file-input-${match.id}`} />
              <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid={`lm-upload-btn-${match.id}`}>
                {uploading ? "Upload en cours…" : "📤 Ou upload un fichier (vidéo mp4 / photo)"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RegistrationCard({ reg, onRefresh }) {
  const [expanded, setExpanded] = useState(false);
  const statusColors = {
    pending: "bg-[#D97706] text-white",
    contacted: "bg-[#1E3A8A] text-white",
    validated: "bg-emerald-600 text-white",
    rejected: "bg-slate-500 text-white",
  };
  const statusLabels = {
    pending: "En attente",
    contacted: "Contacté",
    validated: "Validé",
    rejected: "Refusé",
  };

  const updateStatus = async (status) => {
    try {
      await api.patch(`/registrations/${reg.id}/status`, { status });
      toast.success("Statut mis à jour");
      onRefresh();
    } catch (e) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const del = async () => {
    if (!window.confirm("Supprimer définitivement cette pré-inscription ?")) return;
    try {
      await api.delete(`/registrations/${reg.id}`);
      toast.success("Pré-inscription supprimée");
      onRefresh();
    } catch (e) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const typeLabel = reg.registration_type === "renewal" ? "Renouvellement" : "Nouvelle inscription";
  const createdDate = new Date(reg.created_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });

  return (
    <div className="border border-[#E2E8F0] rounded-md bg-white overflow-hidden" data-testid={`reg-${reg.id}`}>
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full p-4 flex items-start justify-between gap-4 hover:bg-slate-50 transition-colors text-left"
        data-testid={`reg-toggle-${reg.id}`}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`px-2 py-0.5 text-[10px] font-bold rounded ${statusColors[reg.status]}`}>{statusLabels[reg.status]}</span>
            <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-slate-200 text-slate-700">{reg.category}</span>
            {reg.position && <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-[#1E3A8A]/10 text-[#1E3A8A] border border-[#1E3A8A]/20">{reg.position}</span>}
            <span className="px-2 py-0.5 text-[10px] font-bold rounded border border-[#CBD5E1] text-[#475569]">{typeLabel}</span>
            {reg.mutation_needed && <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-[#DC2626] text-white">Mutation</span>}
          </div>
          <div className="mt-2 font-bold text-[#0F172A] truncate" style={{ fontFamily: "Outfit" }}>
            {reg.child_first_name} {reg.child_last_name}
            <span className="text-xs font-normal text-[#475569] ml-2">
              — Parent : {reg.parent_first_name} {reg.parent_last_name}
            </span>
          </div>
          <div className="text-xs text-[#475569] mt-1 truncate">
            {reg.parent_email} · {reg.parent_phone} · {createdDate}
          </div>
        </div>
        <span className="text-xs text-[#1E3A8A] font-semibold shrink-0">{expanded ? "Fermer" : "Détails"}</span>
      </button>
      {expanded && (
        <div className="p-5 bg-slate-50 border-t border-[#E2E8F0] space-y-5" data-testid={`reg-details-${reg.id}`}>
          <div className="grid sm:grid-cols-2 gap-5 text-sm">
            <div>
              <div className="text-[10px] tracking-widest uppercase font-bold text-[#1E3A8A] mb-2">Enfant</div>
              <div className="space-y-1 text-[#475569]">
                <div><span className="font-semibold text-[#0F172A]">Nom :</span> {reg.child_first_name} {reg.child_last_name}</div>
                <div><span className="font-semibold text-[#0F172A]">Né(e) le :</span> {reg.child_birthdate}</div>
                <div><span className="font-semibold text-[#0F172A]">Sexe :</span> {reg.child_gender === "M" ? "Masculin" : reg.child_gender === "F" ? "Féminin" : "—"}</div>
                <div><span className="font-semibold text-[#0F172A]">Catégorie :</span> {reg.category}</div>
                {reg.position && <div><span className="font-semibold text-[#0F172A]">Poste préféré :</span> {reg.position}</div>}
              </div>
            </div>
            <div>
              <div className="text-[10px] tracking-widest uppercase font-bold text-[#1E3A8A] mb-2">Parent / Tuteur</div>
              <div className="space-y-1 text-[#475569]">
                <div><span className="font-semibold text-[#0F172A]">Nom :</span> {reg.parent_first_name} {reg.parent_last_name}</div>
                <div><span className="font-semibold text-[#0F172A]">Email :</span> <a href={`mailto:${reg.parent_email}`} className="text-[#1E3A8A] hover:underline">{reg.parent_email}</a></div>
                <div><span className="font-semibold text-[#0F172A]">Téléphone :</span> <a href={`tel:${reg.parent_phone}`} className="text-[#1E3A8A] hover:underline">{reg.parent_phone}</a></div>
                {(reg.address || reg.postal_code || reg.city) && (
                  <div><span className="font-semibold text-[#0F172A]">Adresse :</span> {[reg.address, reg.postal_code, reg.city].filter(Boolean).join(", ")}</div>
                )}
              </div>
            </div>
            <div className="sm:col-span-2">
              <div className="text-[10px] tracking-widest uppercase font-bold text-[#1E3A8A] mb-2">Historique sportif</div>
              <div className="space-y-1 text-[#475569]">
                <div><span className="font-semibold text-[#0F172A]">Type :</span> {typeLabel}</div>
                <div><span className="font-semibold text-[#0F172A]">Ancien club :</span> {reg.previous_club || "—"}</div>
                <div><span className="font-semibold text-[#0F172A]">Mutation nécessaire :</span> {reg.mutation_needed ? "Oui ⚠️" : "Non"}</div>
              </div>
            </div>
            {reg.notes && (
              <div className="sm:col-span-2">
                <div className="text-[10px] tracking-widest uppercase font-bold text-[#1E3A8A] mb-2">Message</div>
                <div className="p-3 bg-white border border-[#E2E8F0] rounded-md text-[#475569] whitespace-pre-wrap">{reg.notes}</div>
              </div>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-[#E2E8F0]">
            <span className="text-xs font-semibold text-[#475569] mr-2">Changer le statut :</span>
            {["pending", "contacted", "validated", "rejected"].map((s) => (
              <Button
                key={s}
                size="sm"
                variant={reg.status === s ? "default" : "outline"}
                onClick={() => updateStatus(s)}
                data-testid={`reg-status-${s}-${reg.id}`}
                className={reg.status === s ? "bg-[#1E3A8A] hover:bg-[#1E40AF]" : ""}
              >
                {statusLabels[s]}
              </Button>
            ))}
            <Button size="sm" variant="ghost" onClick={del} data-testid={`reg-delete-${reg.id}`} className="ml-auto">
              <Trash2 size={14} className="text-[#DC2626]" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Admin() {
  const [email, setEmail] = useState(localStorage.getItem("vu_admin_email") || "");
  const [password, setPassword] = useState("");
  const [authed, setAuthed] = useState(Boolean(localStorage.getItem("vu_admin_token")));
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ events: [], news: [], faq: [], documents: [], contacts: [], messages: [], stages: [], albums: [], registrations: [], detections: [], socials: [], liveMatches: [] });
  const [alertForm, setAlertForm] = useState({ active: false, message: "", type: "warning", expires_at: "" });
  const [syncing, setSyncing] = useState(false);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/admin/login", { email, password });
      localStorage.setItem("vu_admin_token", r.data.token);
      localStorage.setItem("vu_admin_email", r.data.email);
      setAuthed(true);
      toast.success(`Bienvenue ${r.data.email} !`);
    } catch (err) {
      const status = err.response?.status;
      if (status === 429) {
        toast.error("Trop de tentatives. Attendez 15 minutes.");
      } else {
        toast.error(err.response?.data?.detail || "Erreur de connexion");
      }
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("vu_admin_token");
    // keep email prefilled for convenience
    setAuthed(false);
    setPassword("");
  };

  // Silent token refresh every 24h while active
  useEffect(() => {
    if (!authed) return;
    const refreshInterval = setInterval(async () => {
      try {
        const r = await api.post("/admin/refresh");
        localStorage.setItem("vu_admin_token", r.data.token);
      } catch (e) {
        if (process.env.NODE_ENV !== "production") console.debug("Silent token refresh failed; interceptor handles 401:", e?.response?.status);
      }
    }, 24 * 60 * 60 * 1000);
    return () => clearInterval(refreshInterval);
  }, [authed]);

  const refresh = async () => {
    try {
      const [e, n, f, d, co, m, al, st, ab, reg, det, so, lm] = await Promise.all([
        api.get("/events"),
        api.get("/news"),
        api.get("/faq"),
        api.get("/documents"),
        api.get("/contacts"),
        api.get("/messages").catch(() => ({ data: [] })),
        api.get("/alert").catch(() => ({ data: { active: false, message: "", type: "warning", expires_at: "" } })),
        api.get("/stages/all").catch(() => ({ data: [] })),
        api.get("/albums").catch(() => ({ data: [] })),
        api.get("/registrations").catch(() => ({ data: [] })),
        api.get("/detections").catch(() => ({ data: [] })),
        api.get("/socials").catch(() => ({ data: [] })),
        api.get("/live-matches").catch(() => ({ data: [] })),
      ]);
      setData({ events: e.data, news: n.data, faq: f.data, documents: d.data, contacts: co.data, messages: m.data, stages: st.data, albums: ab.data, registrations: reg.data, detections: det.data, socials: so.data, liveMatches: lm.data });
      setAlertForm({ active: al.data.active || false, message: al.data.message || "", type: al.data.type || "warning", expires_at: al.data.expires_at || "" });
    } catch (err) {
      if (err.response?.status === 401) logout();
    }
  };

  const saveAlert = async () => {
    try {
      await api.put("/alert", alertForm);
      toast.success(alertForm.active ? "Alerte publiée" : "Alerte désactivée");
      refresh();
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const syncFFF = async () => {
    if (!window.confirm("Synchroniser les matchs depuis la FFF ?\n\nCela va remplacer les matchs synchronisés précédemment (vos matchs/entraînements ajoutés manuellement sont conservés).")) return;
    setSyncing(true);
    try {
      const r = await api.post("/events/sync-fff");
      toast.success(`${r.data.inserted} matchs synchronisés depuis la FFF`);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de la synchronisation");
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    if (authed) refresh();
  }, [authed]);

  if (!authed) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4">
        <form onSubmit={login} className="w-full max-w-md bg-white border border-[#E2E8F0] rounded-lg p-8" data-testid="admin-login-form">
          <div className="flex justify-center mb-5">
            <div className="w-14 h-14 rounded-full bg-[#1E3A8A] text-white flex items-center justify-center">
              <Lock size={22} />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-center text-[#0F172A]" style={{ fontFamily: "Outfit" }}>Espace Staff</h1>
          <p className="text-center text-sm text-[#475569] mt-1">Accès réservé au staff du club</p>
          <div className="mt-6">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="admin-email-input" autoFocus autoComplete="email" />
          </div>
          <div className="mt-4">
            <Label htmlFor="password">Mot de passe</Label>
            <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="admin-password-input" autoComplete="current-password" />
          </div>
          <Button type="submit" disabled={loading} className="w-full mt-5 bg-[#1E3A8A] hover:bg-[#1E40AF] h-11" data-testid="admin-login-btn">
            {loading ? "Connexion..." : "Se connecter"}
          </Button>
        </form>
      </div>
    );
  }

  // Generic CRUD helpers
  const create = async (resource, payload) => {
    try {
      await api.post(`/${resource}`, payload);
      toast.success("Créé");
      refresh();
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
      return false;
    }
  };
  const remove = async (resource, id) => {
    if (!window.confirm("Supprimer définitivement ?")) return;
    try {
      await api.delete(`/${resource}/${id}`);
      toast.success("Supprimé");
      refresh();
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const unreadCount = data.messages.filter((m) => !m.read).length;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-dashboard">
      <div className="flex items-center justify-between mb-6">
        <div>
          <span className="text-xs tracking-[0.3em] uppercase text-[#D97706] font-bold">Panneau administrateur</span>
          <h1 className="mt-1 text-3xl font-extrabold text-[#0F172A] tracking-tight" style={{ fontFamily: "Outfit" }}>Pilotage du club</h1>
        </div>
        <Button variant="outline" onClick={logout} data-testid="admin-logout-btn" className="gap-2"><LogOut size={14} /> Déconnexion</Button>
      </div>

      <Tabs defaultValue="alert" className="space-y-6">
        <TabsList className="flex flex-wrap h-auto gap-1 bg-slate-100 p-1">
          <TabsTrigger value="alert" data-testid="tab-alert" className="gap-1.5">
            <AlertTriangle size={13} /> Alerte {alertForm.active && <span className="w-1.5 h-1.5 bg-[#DC2626] rounded-full" />}
          </TabsTrigger>
          <TabsTrigger value="stats" data-testid="tab-stats">📊 Statistiques</TabsTrigger>
          <TabsTrigger value="registrations" data-testid="tab-registrations" className="relative">
            Pré-inscriptions ({data.registrations.length})
            {data.registrations.filter((r) => r.status === "pending").length > 0 && (
              <span className="ml-1 inline-flex items-center justify-center h-5 min-w-[20px] px-1 rounded-full bg-[#D97706] text-white text-[10px] font-bold">
                {data.registrations.filter((r) => r.status === "pending").length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="detections" data-testid="tab-detections">Détections ({data.detections.length})</TabsTrigger>
          <TabsTrigger value="socials" data-testid="tab-socials">Réseaux ({data.socials.length})</TabsTrigger>
          <TabsTrigger value="events" data-testid="tab-events">Calendrier ({data.events.length})</TabsTrigger>
          <TabsTrigger value="liveMatches" data-testid="tab-live-matches" className="gap-1.5">
            <span className="w-1.5 h-1.5 bg-[#DC2626] rounded-full animate-pulse" /> Live Matchs ({data.liveMatches.length})
          </TabsTrigger>
          <TabsTrigger value="news" data-testid="tab-news">Actualités ({data.news.length})</TabsTrigger>
          <TabsTrigger value="faq" data-testid="tab-faq">FAQ ({data.faq.length})</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documents ({data.documents.length})</TabsTrigger>
          <TabsTrigger value="contacts" data-testid="tab-contacts">Contacts ({data.contacts.length})</TabsTrigger>
          <TabsTrigger value="stages" data-testid="tab-stages">Stages ({data.stages.filter((s) => s.active).length}/{data.stages.length})</TabsTrigger>
          <TabsTrigger value="gallery" data-testid="tab-gallery">Galerie ({data.albums.length})</TabsTrigger>
          <TabsTrigger value="messages" data-testid="tab-messages" className="relative">
            Messages ({data.messages.length})
            {unreadCount > 0 && <span className="ml-1 inline-flex items-center justify-center h-5 min-w-[20px] px-1 rounded-full bg-[#DC2626] text-white text-[10px] font-bold">{unreadCount}</span>}
          </TabsTrigger>
        </TabsList>

        {/* ALERT */}
        <TabsContent value="alert">
          <Section title="Bannière d'alerte sur l'accueil">
            <p className="text-sm text-[#475569] mb-5">
              Activez une bannière visible en haut du site pour prévenir rapidement les parents (match annulé, intempéries, information urgente).
              Elle disparaîtra automatiquement à la date d'expiration (facultative).
            </p>
            <div className="grid gap-4">
              <div className="flex items-center gap-3 p-4 border border-[#E2E8F0] rounded-md">
                <input
                  id="alert-active"
                  type="checkbox"
                  checked={alertForm.active}
                  onChange={(e) => setAlertForm({ ...alertForm, active: e.target.checked })}
                  data-testid="alert-active-checkbox"
                  className="w-4 h-4 accent-[#1E3A8A]"
                />
                <Label htmlFor="alert-active" className="cursor-pointer">
                  Activer la bannière sur le site
                </Label>
              </div>
              <div>
                <Label>Type d'alerte</Label>
                <Select value={alertForm.type} onValueChange={(v) => setAlertForm({ ...alertForm, type: v })}>
                  <SelectTrigger data-testid="alert-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="info">Info (bleu)</SelectItem>
                    <SelectItem value="warning">Attention (orange)</SelectItem>
                    <SelectItem value="danger">Urgent (rouge)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="alert-msg">Message</Label>
                <Textarea
                  id="alert-msg"
                  rows={3}
                  value={alertForm.message}
                  onChange={(e) => setAlertForm({ ...alertForm, message: e.target.value })}
                  placeholder="Ex : Match U15 de samedi annulé pour cause d'intempéries."
                  data-testid="alert-message-input"
                />
              </div>
              <div>
                <Label htmlFor="alert-exp">Date d'expiration (facultatif)</Label>
                <Input
                  id="alert-exp"
                  type="datetime-local"
                  value={alertForm.expires_at ? alertForm.expires_at.slice(0, 16) : ""}
                  onChange={(e) => setAlertForm({ ...alertForm, expires_at: e.target.value ? new Date(e.target.value).toISOString() : "" })}
                  data-testid="alert-expires-input"
                />
                <p className="text-xs text-[#475569] mt-1">Après cette date, la bannière disparaîtra automatiquement.</p>
              </div>
              <Button onClick={saveAlert} className="bg-[#1E3A8A] hover:bg-[#1E40AF]" data-testid="alert-save-btn">
                Enregistrer
              </Button>
            </div>
          </Section>
        </TabsContent>

        {/* STATS */}
        <TabsContent value="stats">
          <Section title="📊 Statistiques du site">
            <AnalyticsPanel />
          </Section>
        </TabsContent>

        {/* REGISTRATIONS (Pré-inscriptions) */}
        <TabsContent value="registrations">
          <Section title={`Pré-inscriptions en ligne (${data.registrations.length})`}>
            <p className="text-sm text-[#475569] mb-5">
              Demandes reçues via le formulaire de la page Inscriptions. À chaque nouvelle demande, un email est envoyé automatiquement sur <strong>presidencevufc@yahoo.com</strong>.
            </p>
            {data.registrations.length === 0 ? (
              <div className="p-8 border border-dashed border-[#E2E8F0] rounded-md text-center text-sm text-[#475569]">
                Aucune pré-inscription pour le moment.
              </div>
            ) : (
              <div className="space-y-3">
                {data.registrations.map((r) => (
                  <RegistrationCard key={r.id} reg={r} onRefresh={refresh} />
                ))}
              </div>
            )}
          </Section>
        </TabsContent>

        {/* DETECTIONS */}
        <TabsContent value="detections">
          <Section
            title={`Dates des séances d'essai (${data.detections.length})`}
            action={<CreateDialog label="Ajouter une date" initial={EMPTY.detection} fields={["date", "start_time", "end_time", "categories", "location", "notes"]} onSave={(p) => create("detections", p)} testIdPrefix="detection" />}
          >
            <p className="text-sm text-[#475569] mb-5">
              Ces dates apparaissent en haut de la page <strong>Inscriptions</strong>, dans le bloc "Prochaines dates de détection".
              Dans "Catégories concernées", mettez par exemple <em>"U7 à U11"</em> ou <em>"Toutes catégories"</em>.
            </p>
            {data.detections.length === 0 ? (
              <div className="p-8 border border-dashed border-[#E2E8F0] rounded-md text-center text-sm text-[#475569]">
                Aucune date pour le moment. Ajoutez votre première date via le bouton ci-dessus.
              </div>
            ) : (
              <div className="space-y-2">
                {data.detections.map((d) => (
                  <div key={d.id} className="flex items-center justify-between gap-4 p-3 border border-[#E2E8F0] rounded-md" data-testid={`admin-det-${d.id}`}>
                    <div className="min-w-0">
                      <div className="font-semibold text-[#0F172A]">
                        {new Date(d.date + "T00:00:00").toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                        <span className="ml-2 text-xs font-normal text-[#475569]">{d.start_time}{d.end_time ? ` – ${d.end_time}` : ""}</span>
                      </div>
                      <div className="text-xs text-[#475569] mt-0.5 truncate">
                        {d.categories} · {d.location}{d.notes ? ` · ${d.notes}` : ""}
                      </div>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => remove("detections", d.id)} data-testid={`delete-det-${d.id}`}>
                      <Trash2 size={14} className="text-[#DC2626]" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </Section>
        </TabsContent>

        {/* SOCIALS */}
        <TabsContent value="socials">
          <Section
            title={`Réseaux sociaux (${data.socials.length})`}
            action={<CreateDialog label="Ajouter un réseau" initial={EMPTY.social} fields={["platform_select", "handle", "url", "order"]} onSave={(p) => create("socials", p)} testIdPrefix="social" />}
          >
            <p className="text-sm text-[#475569] mb-5">
              Les liens ajoutés ici apparaissent sur la page publique <strong>/reseaux-sociaux</strong> et dans le pied de page du site.
            </p>
            {data.socials.length === 0 ? (
              <div className="p-8 border border-dashed border-[#E2E8F0] rounded-md text-center text-sm text-[#475569]">
                Aucun réseau pour le moment.
              </div>
            ) : (
              <div className="space-y-2">
                {data.socials.map((s) => (
                  <div key={s.id} className="flex items-center justify-between gap-4 p-3 border border-[#E2E8F0] rounded-md" data-testid={`admin-social-${s.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-[#1E3A8A] text-white uppercase">{s.platform}</span>
                        <span className="font-semibold text-[#0F172A] truncate">{s.handle}</span>
                      </div>
                      <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#1E3A8A] hover:underline mt-0.5 inline-block truncate max-w-full">{s.url}</a>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => remove("socials", s.id)} data-testid={`delete-social-${s.id}`}>
                      <Trash2 size={14} className="text-[#DC2626]" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </Section>
        </TabsContent>

        {/* LIVE MATCHES */}
        <TabsContent value="liveMatches">
          <Section
            title={`Live Matchs (${data.liveMatches.length})`}
            action={
              <CreateDialog
                label="Nouveau match"
                initial={EMPTY.liveMatch}
                fields={["date", "kickoff_time", "lm_category_select", "opponent", "is_home_select", "competition_select", "location", "lm_status_select", "summary"]}
                onSave={(p) => create("live-matches", p)}
                testIdPrefix="lm"
              />
            }
          >
            <p className="text-sm text-[#475569] mb-5">
              Chaque match apparaît sur la page publique <strong>/live-matchs</strong>. Après création, clique sur un match pour <strong>éditer le score, le statut et ajouter des vidéos</strong>.
            </p>
            {data.liveMatches.length === 0 ? (
              <div className="p-8 border border-dashed border-[#E2E8F0] rounded-md text-center text-sm text-[#475569]">
                Aucun match pour le moment. Crée ton premier match avec le bouton ci-dessus.
              </div>
            ) : (
              <div className="space-y-3">
                {data.liveMatches.map((lm) => (
                  <LiveMatchCard key={lm.id} match={lm} onRefresh={refresh} />
                ))}
              </div>
            )}
          </Section>
        </TabsContent>

        {/* EVENTS */}
        <TabsContent value="events">
          <Section
            title="Matchs & Entraînements"
            action={
              <div className="flex gap-2">
                <Button onClick={syncFFF} disabled={syncing} variant="outline" className="gap-2 border-[#D97706] text-[#D97706] hover:bg-[#D97706] hover:text-white" data-testid="sync-fff-btn">
                  <RefreshCw size={14} className={syncing ? "animate-spin" : ""} /> {syncing ? "Synchro..." : "Synchroniser FFF"}
                </Button>
                <CreateDialog label="Ajouter un événement" initial={EMPTY.event} fields={["type_select", "title", "date", "time", "location", "opponent", "category_select", "notes"]} onSave={(p) => create("events", p)} testIdPrefix="event" />
              </div>
            }
          >
            <p className="text-xs text-[#475569] mb-4 flex items-center gap-2">
              <RefreshCw size={11} /> Les matchs <span className="font-bold text-[#D97706]">FFF</span> (badge orange) sont auto-mis à jour depuis sportcorico.com. Vos matchs/entraînements <span className="font-bold">manuels</span> sont préservés.
            </p>
            <div className="space-y-2">
              {data.events.map((e) => (
                <div key={e.id} className="flex items-center justify-between gap-4 p-3 border border-[#E2E8F0] rounded-md" data-testid={`admin-event-${e.id}`}>
                  <div className="flex items-center gap-3 min-w-0 flex-wrap">
                    <span className={`px-2 py-0.5 text-[10px] font-bold rounded ${e.type === "match" ? "bg-[#DC2626] text-white" : "bg-[#1E3A8A] text-white"}`}>{e.type}</span>
                    {e.source === "fff" && <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-[#D97706] text-white">FFF</span>}
                    {e.status === "played" && <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-slate-600 text-white">Joué {e.vufc_score}-{e.opp_score}</span>}
                    <span className="text-xs text-[#475569]">{e.date} {e.time}</span>
                    <span className="font-semibold text-[#0F172A] truncate">{e.title}</span>
                    <span className="text-xs text-[#475569]">{e.category}</span>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => remove("events", e.id)} data-testid={`delete-event-${e.id}`}><Trash2 size={14} className="text-[#DC2626]" /></Button>
                </div>
              ))}
            </div>
          </Section>
        </TabsContent>

        {/* NEWS */}
        <TabsContent value="news">
          <Section title="Actualités" action={<CreateDialog label="Nouvelle actualité" initial={EMPTY.news} fields={["title", "excerpt", "content", "image_url", "author"]} onSave={(p) => create("news", p)} testIdPrefix="news" />}>
            <div className="space-y-2">
              {data.news.map((n) => (
                <div key={n.id} className="flex items-center justify-between gap-4 p-3 border border-[#E2E8F0] rounded-md" data-testid={`admin-news-${n.id}`}>
                  <div className="min-w-0">
                    <div className="font-semibold text-[#0F172A] truncate">{n.title}</div>
                    <div className="text-xs text-[#475569]">{new Date(n.published_at).toLocaleDateString("fr-FR")} · par {n.author}</div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => remove("news", n.id)} data-testid={`delete-news-${n.id}`}><Trash2 size={14} className="text-[#DC2626]" /></Button>
                </div>
              ))}
            </div>
          </Section>
        </TabsContent>

        {/* FAQ */}
        <TabsContent value="faq">
          <Section title="Foire aux questions" action={<CreateDialog label="Ajouter une Q&R" initial={EMPTY.faq} fields={["question", "answer", "category", "order"]} onSave={(p) => create("faq", p)} testIdPrefix="faq" />}>
            <div className="space-y-2">
              {data.faq.map((f) => (
                <div key={f.id} className="flex items-start justify-between gap-4 p-3 border border-[#E2E8F0] rounded-md" data-testid={`admin-faq-${f.id}`}>
                  <div className="min-w-0">
                    <div className="font-semibold text-[#0F172A]">{f.question}</div>
                    <div className="text-xs text-[#475569] mt-0.5">Catégorie: {f.category}</div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => remove("faq", f.id)} data-testid={`delete-faq-${f.id}`}><Trash2 size={14} className="text-[#DC2626]" /></Button>
                </div>
              ))}
            </div>
          </Section>
        </TabsContent>

        {/* DOCUMENTS */}
        <TabsContent value="documents">
          <Section title="Documents" action={<CreateDialog label="Ajouter un document" initial={EMPTY.document} fields={["title", "description", "url", "category"]} onSave={(p) => create("documents", p)} testIdPrefix="document" />}>
            <div className="space-y-2">
              {data.documents.map((d) => (
                <div key={d.id} className="flex items-center justify-between gap-4 p-3 border border-[#E2E8F0] rounded-md" data-testid={`admin-doc-${d.id}`}>
                  <div className="min-w-0">
                    <div className="font-semibold text-[#0F172A] truncate">{d.title}</div>
                    <div className="text-xs text-[#475569]">{d.category} · {d.url}</div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => remove("documents", d.id)} data-testid={`delete-doc-${d.id}`}><Trash2 size={14} className="text-[#DC2626]" /></Button>
                </div>
              ))}
            </div>
          </Section>
        </TabsContent>

        {/* CONTACTS */}
        <TabsContent value="contacts">
          <Section title="Contacts du club" action={<CreateDialog label="Ajouter un contact" initial={EMPTY.contact} fields={["name", "role", "phone", "email", "photo_url", "order"]} onSave={(p) => create("contacts", p)} testIdPrefix="contact" />}>
            <div className="space-y-2">
              {data.contacts.map((c) => (
                <div key={c.id} className="flex items-center justify-between gap-4 p-3 border border-[#E2E8F0] rounded-md" data-testid={`admin-contact-${c.id}`}>
                  <div className="min-w-0">
                    <div className="font-semibold text-[#0F172A] truncate">{c.name} <span className="text-xs text-[#475569]">— {c.role}</span></div>
                    <div className="text-xs text-[#475569]">{c.phone} · {c.email}</div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => remove("contacts", c.id)} data-testid={`delete-contact-${c.id}`}><Trash2 size={14} className="text-[#DC2626]" /></Button>
                </div>
              ))}
            </div>
          </Section>
        </TabsContent>

        {/* STAGES */}
        <TabsContent value="stages">
          <Section title="Stages de foot (vacances scolaires)">
            <p className="text-xs text-[#475569] mb-4">
              Chaque stage est pré-rempli. <span className="font-bold">Activez-le</span> pour qu'il apparaisse sur la page publique /stages, puis complétez les horaires et ajoutez le lien du dossier d'inscription (PDF).
            </p>
            <div className="space-y-3">
              {data.stages.map((s) => (
                <StageEditor key={s.id} stage={s} onSaved={refresh} onDelete={() => remove("stages", s.id)} />
              ))}
            </div>
          </Section>
        </TabsContent>

        {/* GALLERY */}
        <TabsContent value="gallery">
          <Section
            title="Albums photos"
            action={<CreateDialog label="Nouvel album" initial={{ name: "", description: "" }} fields={["name", "description"]} onSave={async (p) => { try { await api.post("/albums", p); toast.success("Album créé"); refresh(); return true; } catch { toast.error("Erreur"); return false; } }} testIdPrefix="album" />}
          >
            <p className="text-xs text-[#475569] mb-4">
              Créez un album par événement (ex: « Tournoi U11 — mars 2026 », « Photo d'équipe U15 »).
              Cliquez sur un album pour y uploader vos photos (JPEG/PNG/WebP, max 8 Mo chacune).
            </p>
            {data.albums.length === 0 && <div className="text-sm text-[#475569]">Aucun album. Créez le premier avec le bouton ci-dessus.</div>}
            <div className="space-y-3">
              {data.albums.map((a) => (
                <AlbumManager key={a.id} album={a} onChange={refresh} />
              ))}
            </div>
          </Section>
        </TabsContent>

        {/* MESSAGES */}
        <TabsContent value="messages">
          <Section title="Messages reçus">
            <div className="space-y-3">
              {data.messages.length === 0 && <div className="text-sm text-[#475569]">Aucun message pour l'instant.</div>}
              {data.messages.map((m) => (
                <div key={m.id} className={`p-4 border rounded-md ${m.read ? "border-[#E2E8F0] bg-white" : "border-[#DC2626]/40 bg-red-50/40"}`} data-testid={`admin-message-${m.id}`}>
                  <div className="flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <div className="font-semibold text-[#0F172A] flex items-center gap-2">
                        {!m.read && <span className="w-2 h-2 bg-[#DC2626] rounded-full" />}
                        {m.subject}
                      </div>
                      <div className="text-xs text-[#475569]">
                        <Mail size={10} className="inline mr-1" />
                        {m.parent_name} {m.child_name && `(parent de ${m.child_name})`} · {m.email} · {m.phone}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      {!m.read && (
                        <Button size="sm" variant="ghost" onClick={async () => { await api.patch(`/messages/${m.id}/read`); refresh(); }} data-testid={`mark-read-${m.id}`}><Check size={14} className="text-[#16a34a]" /></Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => remove("messages", m.id)} data-testid={`delete-message-${m.id}`}><Trash2 size={14} className="text-[#DC2626]" /></Button>
                    </div>
                  </div>
                  <div className="mt-2 text-sm text-[#0F172A] whitespace-pre-wrap">{m.message}</div>
                  <div className="mt-2 text-[10px] text-[#475569]">{new Date(m.created_at).toLocaleString("fr-FR")}</div>
                </div>
              ))}
            </div>
          </Section>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function CreateDialog({ label, initial, fields, onSave, testIdPrefix }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initial);
  const [playersText, setPlayersText] = useState("");

  const update = (k, v) => setForm({ ...form, [k]: v });

  const save = async () => {
    const payload = { ...form };
    if (fields.includes("players_list")) {
      payload.players = playersText.split("\n").map((s) => s.trim()).filter(Boolean);
    }
    if (fields.includes("order")) payload.order = parseInt(payload.order || 0, 10);
    const ok = await onSave(payload);
    if (ok) {
      setOpen(false);
      setForm(initial);
      setPlayersText("");
    }
  };

  const renderField = (f) => {
    switch (f) {
      case "type_select":
        return (
          <div key={f}>
            <Label>Type</Label>
            <Select value={form.type} onValueChange={(v) => update("type", v)}>
              <SelectTrigger data-testid={`${testIdPrefix}-type`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="match">Match</SelectItem>
                <SelectItem value="entrainement">Entraînement</SelectItem>
              </SelectContent>
            </Select>
          </div>
        );
      case "category_select":
        return (
          <div key={f}>
            <Label>Catégorie</Label>
            <Select value={form.category} onValueChange={(v) => update("category", v)}>
              <SelectTrigger data-testid={`${testIdPrefix}-category`}><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        );
      case "platform_select":
        return (
          <div key={f}>
            <Label>Plateforme</Label>
            <Select value={form.platform} onValueChange={(v) => update("platform", v)}>
              <SelectTrigger data-testid={`${testIdPrefix}-platform`}><SelectValue /></SelectTrigger>
              <SelectContent>
                {["tiktok", "instagram", "facebook", "youtube", "twitter", "linkedin", "other"].map((p) =>
                  <SelectItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>
        );
      case "lm_category_select":
        return (
          <div key={f}>
            <Label>Catégorie</Label>
            <Select value={form.category} onValueChange={(v) => update("category", v)}>
              <SelectTrigger data-testid={`${testIdPrefix}-category`}><SelectValue placeholder="Choisir…" /></SelectTrigger>
              <SelectContent>
                {["U7", "U9", "U11", "U13", "U15", "U17", "Seniors", "Loisirs", "Foot Loisirs"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        );
      case "is_home_select":
        return (
          <div key={f}>
            <Label>Lieu</Label>
            <Select value={String(form.is_home)} onValueChange={(v) => update("is_home", v === "true")}>
              <SelectTrigger data-testid={`${testIdPrefix}-home`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="true">À domicile</SelectItem>
                <SelectItem value="false">À l'extérieur</SelectItem>
              </SelectContent>
            </Select>
          </div>
        );
      case "competition_select":
        return (
          <div key={f}>
            <Label>Compétition</Label>
            <Select value={form.competition} onValueChange={(v) => update("competition", v)}>
              <SelectTrigger data-testid={`${testIdPrefix}-competition`}><SelectValue /></SelectTrigger>
              <SelectContent>
                {["Championnat", "Coupe", "Amical", "Tournoi"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        );
      case "lm_status_select":
        return (
          <div key={f}>
            <Label>Statut</Label>
            <Select value={form.status} onValueChange={(v) => update("status", v)}>
              <SelectTrigger data-testid={`${testIdPrefix}-status`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="scheduled">À venir</SelectItem>
                <SelectItem value="live">🔴 En direct</SelectItem>
                <SelectItem value="finished">Terminé</SelectItem>
              </SelectContent>
            </Select>
          </div>
        );
      case "players_list":
        return (
          <div key={f} className="col-span-2">
            <Label>Joueurs (un par ligne)</Label>
            <Textarea rows={6} value={playersText} onChange={(e) => setPlayersText(e.target.value)} data-testid={`${testIdPrefix}-players`} placeholder={"Lucas Martin\nIbrahim Keita\n..."} />
          </div>
        );
      case "content":
      case "notes":
      case "answer":
      case "description":
      case "excerpt":
        return (
          <div key={f} className="col-span-2">
            <Label htmlFor={f}>{labelOf(f)}</Label>
            <Textarea id={f} rows={f === "content" || f === "answer" ? 6 : 3} value={form[f] || ""} onChange={(e) => update(f, e.target.value)} data-testid={`${testIdPrefix}-${f}`} />
          </div>
        );
      case "date":
      case "match_date":
        return (
          <div key={f}>
            <Label htmlFor={f}>{labelOf(f)}</Label>
            <Input id={f} type="date" value={form[f] || ""} onChange={(e) => update(f, e.target.value)} data-testid={`${testIdPrefix}-${f}`} />
          </div>
        );
      case "time":
      case "match_time":
      case "meeting_time":
      case "start_time":
      case "end_time":
        return (
          <div key={f}>
            <Label htmlFor={f}>{labelOf(f)}</Label>
            <Input id={f} type="time" value={form[f] || ""} onChange={(e) => update(f, e.target.value)} data-testid={`${testIdPrefix}-${f}`} />
          </div>
        );
      default:
        return (
          <div key={f}>
            <Label htmlFor={f}>{labelOf(f)}</Label>
            <Input id={f} value={form[f] || ""} onChange={(e) => update(f, e.target.value)} data-testid={`${testIdPrefix}-${f}`} />
          </div>
        );
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-[#1E3A8A] hover:bg-[#1E40AF] gap-2" data-testid={`${testIdPrefix}-add-btn`}><Plus size={14} /> {label}</Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }}>{label}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          {fields.map(renderField)}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
          <Button onClick={save} className="bg-[#1E3A8A] hover:bg-[#1E40AF]" data-testid={`${testIdPrefix}-save-btn`}>Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function labelOf(f) {
  const map = {
    title: "Titre",
    excerpt: "Résumé",
    content: "Contenu",
    image_url: "URL de l'image",
    author: "Auteur",
    date: "Date",
    time: "Heure",
    location: "Lieu",
    opponent: "Adversaire",
    notes: "Notes",
    question: "Question",
    answer: "Réponse",
    category: "Catégorie",
    order: "Ordre",
    description: "Description",
    url: "Lien (URL)",
    name: "Nom",
    role: "Rôle",
    phone: "Téléphone",
    email: "Email",
    photo_url: "URL photo",
    match_title: "Nom du match",
    match_date: "Date du match",
    match_time: "Heure du match",
    meeting_time: "Heure de RDV",
    meeting_place: "Lieu de RDV",
    start_time: "Heure de début",
    end_time: "Heure de fin",
    categories: "Catégories concernées",
    handle: "Nom d'utilisateur / @handle",
    url: "URL complète du profil",
  };
  return map[f] || f;
}

function StageEditor({ stage, onSaved, onDelete }) {
  const [form, setForm] = useState({
    name: stage.name || "",
    start_date: stage.start_date || "",
    end_date: stage.end_date || "",
    time_slot: stage.time_slot || "",
    categories: stage.categories || "",
    price_eur: stage.price_eur ?? 100,
    lunch: stage.lunch || "",
    location: stage.location || "",
    document_url: stage.document_url || "",
    description: stage.description || "",
    active: Boolean(stage.active),
  });
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);

  const update = (k, v) => setForm({ ...form, [k]: v });

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/stages/${stage.id}`, { ...form, price_eur: parseInt(form.price_eur || 0, 10) });
      toast.success("Stage enregistré");
      onSaved();
      setOpen(false);
    } catch (e) {
      toast.error("Erreur");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async () => {
    setSaving(true);
    try {
      await api.put(`/stages/${stage.id}`, { ...form, active: !form.active, price_eur: parseInt(form.price_eur || 0, 10) });
      setForm({ ...form, active: !form.active });
      toast.success(form.active ? "Stage retiré du site" : "Stage publié sur le site");
      onSaved();
    } catch (e) {
      toast.error("Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`border rounded-md ${form.active ? "border-[#D97706] bg-[#FEF3C7]/30" : "border-[#E2E8F0] bg-white"}`} data-testid={`admin-stage-${stage.id}`}>
      <div className="flex items-center justify-between gap-4 p-4">
        <div className="min-w-0 flex items-center gap-3">
          {form.active ? (
            <span className="shrink-0 px-2 py-0.5 text-[10px] font-bold rounded bg-[#D97706] text-white">PUBLIÉ</span>
          ) : (
            <span className="shrink-0 px-2 py-0.5 text-[10px] font-bold rounded bg-slate-300 text-slate-700">BROUILLON</span>
          )}
          <div className="min-w-0">
            <div className="font-semibold text-[#0F172A] truncate">{form.name}</div>
            <div className="text-xs text-[#475569]">{form.start_date} → {form.end_date} · {form.categories} · {form.price_eur} €</div>
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button size="sm" variant={form.active ? "default" : "outline"} onClick={toggleActive} disabled={saving} className={form.active ? "bg-[#D97706] hover:bg-[#B45309]" : ""} data-testid={`toggle-stage-${stage.id}`}>
            {form.active ? "Retirer" : "Publier"}
          </Button>
          <Button size="sm" variant="outline" onClick={() => setOpen(!open)} data-testid={`edit-stage-${stage.id}`}>
            {open ? "Fermer" : "Modifier"}
          </Button>
          <Button size="sm" variant="ghost" onClick={onDelete} data-testid={`delete-stage-${stage.id}`}>
            <Trash2 size={14} className="text-[#DC2626]" />
          </Button>
        </div>
      </div>
      {open && (
        <div className="border-t border-[#E2E8F0] p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Nom</Label><Input value={form.name} onChange={(e) => update("name", e.target.value)} data-testid={`stage-name-${stage.id}`} /></div>
            <div><Label>Prix (€)</Label><Input type="number" value={form.price_eur} onChange={(e) => update("price_eur", e.target.value)} data-testid={`stage-price-${stage.id}`} /></div>
            <div><Label>Début</Label><Input type="date" value={form.start_date} onChange={(e) => update("start_date", e.target.value)} data-testid={`stage-start-${stage.id}`} /></div>
            <div><Label>Fin</Label><Input type="date" value={form.end_date} onChange={(e) => update("end_date", e.target.value)} data-testid={`stage-end-${stage.id}`} /></div>
            <div className="col-span-2"><Label>Horaires (ex: 9h-12h / 14h-17h)</Label><Input value={form.time_slot} onChange={(e) => update("time_slot", e.target.value)} data-testid={`stage-time-${stage.id}`} /></div>
            <div><Label>Catégories</Label><Input value={form.categories} onChange={(e) => update("categories", e.target.value)} /></div>
            <div><Label>Repas</Label><Input value={form.lunch} onChange={(e) => update("lunch", e.target.value)} /></div>
            <div className="col-span-2"><Label>Lieu</Label><Input value={form.location} onChange={(e) => update("location", e.target.value)} /></div>
            <div className="col-span-2"><Label>Lien du dossier d'inscription (PDF)</Label><Input value={form.document_url} onChange={(e) => update("document_url", e.target.value)} placeholder="https://..." data-testid={`stage-doc-${stage.id}`} /></div>
            <div className="col-span-2"><Label>Description (facultatif)</Label><Textarea rows={3} value={form.description} onChange={(e) => update("description", e.target.value)} /></div>
          </div>
          <Button onClick={save} disabled={saving} className="bg-[#1E3A8A] hover:bg-[#1E40AF]" data-testid={`save-stage-${stage.id}`}>Enregistrer</Button>
        </div>
      )}
    </div>
  );
}

function AlbumManager({ album, onChange }) {
  const [open, setOpen] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);

  const loadPhotos = async () => {
    try {
      const r = await api.get(`/albums/${album.slug}`);
      setPhotos(r.data.photos);
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.debug("Album photos load failed:", e?.response?.status);
    }
  };

  useEffect(() => { if (open) loadPhotos(); // eslint-disable-next-line
  }, [open]);

  const upload = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setUploading(true);
    let done = 0;
    try {
      for (const file of Array.from(fileList)) {
        const fd = new FormData();
        fd.append("album_id", album.id);
        fd.append("caption", "");
        fd.append("file", file);
        try {
          await api.post("/photos/upload", fd, { headers: { "Content-Type": undefined } });
          done++;
        } catch (err) {
          toast.error(`${file.name}: ${err.response?.data?.detail || "upload échoué"}`);
        }
      }
      if (done > 0) toast.success(`${done} photo${done > 1 ? "s" : ""} ajoutée${done > 1 ? "s" : ""}`);
      await loadPhotos();
      onChange();
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = async (pid) => {
    if (!window.confirm("Supprimer cette photo ?")) return;
    try {
      await api.delete(`/photos/${pid}`);
      await loadPhotos();
      onChange();
      toast.success("Photo supprimée");
    } catch { toast.error("Erreur"); }
  };

  const removeAlbum = async () => {
    if (!window.confirm(`Supprimer l'album « ${album.name} » et toutes ses photos ?`)) return;
    try {
      await api.delete(`/albums/${album.id}`);
      toast.success("Album supprimé");
      onChange();
    } catch { toast.error("Erreur"); }
  };

  return (
    <div className="rounded-md border border-[#E2E8F0] bg-white" data-testid={`admin-album-${album.id}`}>
      <div className="flex items-center justify-between gap-3 p-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-12 h-12 shrink-0 rounded-md overflow-hidden bg-slate-100 flex items-center justify-center">
            {album.cover_photo_id ? (
              <img src={`${API}/photos/${album.cover_photo_id}/image`} alt="" className="w-full h-full object-cover" />
            ) : (
              <ImageIcon size={18} className="text-slate-400" />
            )}
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-[#0F172A] truncate">{album.name}</div>
            <div className="text-xs text-[#475569]">{album.photo_count} photo{album.photo_count > 1 ? "s" : ""} · /{album.slug}</div>
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button size="sm" variant="outline" onClick={() => setOpen(!open)} data-testid={`toggle-album-${album.id}`}>
            {open ? "Fermer" : "Gérer les photos"}
          </Button>
          <Button size="sm" variant="ghost" onClick={removeAlbum} data-testid={`delete-album-${album.id}`}>
            <Trash2 size={14} className="text-[#DC2626]" />
          </Button>
        </div>
      </div>
      {open && (
        <div className="border-t border-[#E2E8F0] p-4">
          <label className="block mb-4">
            <div className={`cursor-pointer rounded-lg border-2 border-dashed ${uploading ? "border-[#D97706] bg-[#FEF3C7]/50" : "border-[#E2E8F0] bg-slate-50 hover:border-[#1E3A8A] hover:bg-slate-100"} p-6 text-center transition-colors`}>
              <Upload className="h-6 w-6 text-[#1E3A8A] mx-auto mb-2" />
              <div className="text-sm font-semibold text-[#0F172A]">
                {uploading ? "Upload en cours..." : "Cliquez pour ajouter des photos"}
              </div>
              <div className="text-xs text-[#475569] mt-1">JPEG, PNG, WebP · max 8 Mo par image · plusieurs photos à la fois ok</div>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                multiple
                className="hidden"
                disabled={uploading}
                onChange={(e) => upload(e.target.files)}
                data-testid={`upload-input-${album.id}`}
              />
            </div>
          </label>
          {photos.length === 0 ? (
            <div className="text-sm text-[#475569] text-center py-4">Aucune photo dans cet album.</div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2">
              {photos.map((p) => (
                <div key={p.id} className="relative group aspect-square rounded-md overflow-hidden bg-slate-100" data-testid={`admin-photo-${p.id}`}>
                  <img src={`${API}/photos/${p.id}/image`} alt="" className="w-full h-full object-cover" loading="lazy" />
                  <button
                    onClick={() => removePhoto(p.id)}
                    className="absolute top-1 right-1 w-7 h-7 rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                    aria-label="Supprimer"
                    data-testid={`delete-photo-${p.id}`}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
